import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { irohNodesApi } from "@/api/routes/iroh-nodes"
import { Tag as IrohNodesTag } from "@/api/tags"
import { connectWs } from "@/api/ws"
import type { ProjectIdParam, Uuid } from "@/types"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NetDiagnostic {
  id: Uuid
  project_id: Uuid
  endpoint_id: string
  report: DiagnosticsReport
  created_at: string
  nat_type: NatType
}

export interface DiagnosticsReport {
  endpoint_id: string
  net_report: NetReport | null
  direct_addrs: string[]
  portmap_probe: PortMapProbe | null
  iroh_version: string
  iroh_services_version: string
}

export interface NetReport {
  udp_v4: boolean
  udp_v6: boolean
  global_v4: string | null
  global_v6: string | null
  mapping_varies_by_dest_ipv4: boolean | null
  mapping_varies_by_dest_ipv6: boolean | null
  preferred_relay: string | null
  relay_latency: RelayLatencies
  captive_portal: boolean | null
}

/** Duration values from serde: BTreeMap<RelayUrl, Duration> where Duration = {secs, nanos} */
export interface RelayLatencies {
  ipv4: Record<string, { secs: number; nanos: number }>
  ipv6: Record<string, { secs: number; nanos: number }>
  https: Record<string, { secs: number; nanos: number }>
}

export interface PortMapProbe {
  upnp: boolean
  pcp: boolean
  nat_pmp: boolean
}

/** Convert a serde Duration ({secs, nanos}) to milliseconds */
export function durationToMs(d: { secs: number; nanos: number }): number {
  return d.secs * 1000 + d.nanos / 1_000_000
}

// ---------------------------------------------------------------------------
// NAT classification — computed by the backend, display info only here
// ---------------------------------------------------------------------------

export type NatType =
  | "No NAT"
  | "Endpoint-Independent"
  | "Endpoint-Dependent"
  | "Unknown"

export const natTypeInfo: Record<
  NatType,
  { description: string; badgeClass: string }
> = {
  "No NAT": {
    description:
      "No NAT detected — the endpoint's local address matches its public address. With the correct firewall configuration, no holepunching is required.",
    badgeClass: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
  },
  "Endpoint-Independent": {
    description:
      "Endpoint-independent NAT — the most common case for home networks. One outbound UDP packet opens a port for incoming traffic from any address. Holepunching works reliably.",
    badgeClass:
      "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
  },
  "Endpoint-Dependent": {
    description:
      "Endpoint-dependent NAT (symmetric) — only the specific address that was sent to is allowed to send traffic back. Direct connections require port prediction. Connections will primarily use relays.",
    badgeClass:
      "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200",
  },
  Unknown: {
    description:
      "NAT behavior could not be determined from the available data.",
    badgeClass: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  },
}

/** Safe lookup that falls back to "Unknown" if the backend returns a NAT type the frontend doesn't know about. */
export function natInfoFor(natType: string): {
  description: string
  badgeClass: string
} {
  return natTypeInfo[natType as NatType] ?? natTypeInfo.Unknown
}

/** Per-endpoint online/capability status returned by GET /online-endpoints */
export interface EndpointStatus {
  online: boolean
  has_cap: boolean
}

export interface ReportCounts {
  total: number
  by_endpoint: Record<string, number>
}

/** Map of endpoint_id → status */
export type EndpointStatusMap = Record<string, EndpointStatus>

/** Incremental WS message for endpoint activity */
interface EndpointActivityMessage {
  type: "connected" | "disconnected" | "cap_changed" | "metadata_changed"
  endpoints: string[]
}

enum Tag {
  Reports = "NetDiagnosticsReports",
  Counts = "NetDiagnosticsCounts",
  EndpointCounts = "NetDiagnosticsEndpointCounts",
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const netDiagnosticsApi = createApi({
  reducerPath: "netDiagnostics",
  tagTypes: Object.values(Tag),
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}net-diagnostics`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    // -----------------------------------------------------------------------
    // Online endpoints (status map + WS incremental updates)
    // -----------------------------------------------------------------------
    onlineEndpointsWs: build.query<EndpointStatusMap, ProjectIdParam>({
      query: ({ project_id }) => `${project_id}/online-endpoints`,

      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) {
        await cacheDataLoaded

        // Re-fetch the full status map to pick up has_cap for new endpoints
        const refetchStatus = async () => {
          try {
            const res = await fetch(
              `${IROH_SERVICES_API_PREFIX}net-diagnostics/${args.project_id}/online-endpoints`,
              { credentials: "include" },
            )
            if (!res.ok) return
            const fresh: EndpointStatusMap = await res.json()
            updateCachedData(() => fresh)
          } catch {
            // best-effort
          }
        }

        // Metadata events arrive one per endpoint, and each list refetch is
        // project-wide. A fleet re-announcing metadata after a rollout would
        // trigger one full refetch per endpoint, so collect the ids and
        // invalidate once per quiet half-second instead.
        let pendingMetadataIds = new Set<string>()
        let metadataFlushTimer: ReturnType<typeof setTimeout> | undefined
        const flushMetadataInvalidation = () => {
          metadataFlushTimer = undefined
          const ids = Array.from(pendingMetadataIds)
          pendingMetadataIds = new Set()
          dispatch(
            irohNodesApi.util.invalidateTags([
              ...ids.map((id) => ({ type: IrohNodesTag.IrohNodes, id })),
              { type: IrohNodesTag.IrohNodes, id: args.project_id },
            ]),
          )
        }

        const cleanup = connectWs(
          `${IROH_SERVICES_API_PREFIX}net-diagnostics/${args.project_id}/online-endpoints/ws/`,
          (event: MessageEvent) => {
            let msg: EndpointActivityMessage
            try {
              msg = JSON.parse(event.data)
            } catch {
              return
            }
            // Metadata changes (name/group/attributes) aren't part of the
            // online-status cache; invalidate the iroh-nodes cache (debounced)
            // so the endpoints list and detail page refetch.
            if (msg.type === "metadata_changed") {
              for (const id of msg.endpoints) {
                pendingMetadataIds.add(id)
              }
              metadataFlushTimer ??= setTimeout(flushMetadataInvalidation, 500)
              return
            }
            let needsRefetch = false
            updateCachedData((draft) => {
              for (const id of msg.endpoints) {
                if (msg.type === "connected") {
                  if (!(id in draft)) {
                    needsRefetch = true
                  }
                  draft[id] = {
                    online: true,
                    has_cap: draft[id]?.has_cap ?? false,
                  }
                } else if (msg.type === "cap_changed") {
                  needsRefetch = true
                } else if (id in draft) {
                  draft[id] = { ...draft[id], online: false }
                }
              }
            })
            if (needsRefetch) {
              refetchStatus()
            }
          },
        )
        await cacheEntryRemoved
        cleanup()
        if (metadataFlushTimer !== undefined) {
          clearTimeout(metadataFlushTimer)
        }
      },
    }),

    // -----------------------------------------------------------------------
    // Reports list (with WS for new reports)
    // -----------------------------------------------------------------------
    reportsWs: build.query<NetDiagnostic[], ProjectIdParam>({
      query: ({ project_id }) => ({
        url: project_id,
        params: { limit: 50, offset: 0 },
      }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.Reports, id: project_id },
      ],

      async onCacheEntryAdded(
        args,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded

        const cleanup = connectWs(
          `${IROH_SERVICES_API_PREFIX}net-diagnostics/${args.project_id}/reports/ws/`,
          (event: MessageEvent) => {
            let nd: NetDiagnostic
            try {
              nd = JSON.parse(event.data)
            } catch {
              return
            }
            let inserted = false
            updateCachedData((draft) => {
              // Prepend new report so newest is first, with id-based dedupe.
              if (draft.some((r) => r.id === nd.id)) return
              draft.unshift(nd)
              inserted = true
            })
            if (!inserted) return
            dispatch(
              netDiagnosticsApi.util.invalidateTags([
                { type: Tag.Counts, id: args.project_id },
                { type: Tag.EndpointCounts, id: args.project_id },
                {
                  type: Tag.EndpointCounts,
                  id: `${args.project_id}:${nd.endpoint_id}`,
                },
              ]),
            )
            // The node list carries `latest_iroh_version`, which is enriched
            // from the newest report — so a new report makes it stale too.
            dispatch(
              irohNodesApi.util.invalidateTags([
                { type: IrohNodesTag.IrohNodes, id: nd.endpoint_id },
                { type: IrohNodesTag.IrohNodes, id: args.project_id },
              ]),
            )
          },
        )
        await cacheEntryRemoved
        cleanup()
      },
    }),

    // -----------------------------------------------------------------------
    // Report counts
    // -----------------------------------------------------------------------
    reportCounts: build.query<ReportCounts, ProjectIdParam>({
      query: ({ project_id }) => `${project_id}/counts`,
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.Counts, id: project_id },
      ],
    }),

    endpointReportCount: build.query<
      number,
      ProjectIdParam & { endpoint_id: string }
    >({
      query: ({ project_id, endpoint_id }) =>
        `${project_id}/counts/${endpoint_id}`,
      providesTags: (_result, _error, { project_id, endpoint_id }) => [
        { type: Tag.EndpointCounts, id: project_id },
        {
          type: Tag.EndpointCounts,
          id: `${project_id}:${endpoint_id}`,
        },
      ],
    }),

    // -----------------------------------------------------------------------
    // Latest report for a specific endpoint (limit=1)
    // -----------------------------------------------------------------------
    latestEndpointReport: build.query<
      NetDiagnostic | undefined,
      ProjectIdParam & { endpoint_id: string }
    >({
      query: ({ project_id, endpoint_id }) => ({
        url: project_id,
        params: { limit: 1, offset: 0, endpoint_id },
      }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.Reports, id: project_id },
      ],
      transformResponse: (response: NetDiagnostic[]) => response[0],
    }),

    // -----------------------------------------------------------------------
    // Reports list for a specific endpoint
    // -----------------------------------------------------------------------
    endpointReports: build.query<
      NetDiagnostic[],
      ProjectIdParam & { endpoint_id: string; limit?: number; offset?: number }
    >({
      query: ({ project_id, endpoint_id, limit = 25, offset = 0 }) => ({
        url: project_id,
        params: { limit, offset, endpoint_id },
      }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.Reports, id: project_id },
      ],
    }),

    // -----------------------------------------------------------------------
    // Single report
    // -----------------------------------------------------------------------
    getReport: build.query<NetDiagnostic, ProjectIdParam & { report_id: Uuid }>(
      {
        query: ({ project_id, report_id }) => `${project_id}/${report_id}`,
      },
    ),

    // -----------------------------------------------------------------------
    // Trigger diagnostic on a remote endpoint
    // -----------------------------------------------------------------------
    runDiagnostic: build.mutation<
      NetDiagnostic,
      ProjectIdParam & { endpoint_id: string }
    >({
      query: ({ project_id, endpoint_id }) => ({
        url: `${project_id}/run/${endpoint_id}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { project_id, endpoint_id }) => [
        { type: Tag.Reports, id: project_id },
        { type: Tag.Counts, id: project_id },
        { type: Tag.EndpointCounts, id: project_id },
        {
          type: Tag.EndpointCounts,
          id: `${project_id}:${endpoint_id}`,
        },
      ],
      async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: nd } = await queryFulfilled
          dispatch(
            netDiagnosticsApi.util.updateQueryData(
              "reportsWs",
              { project_id },
              (draft) => {
                if (draft.some((r) => r.id === nd.id)) return
                draft.unshift(nd)
              },
            ),
          )
        } catch {
          // no-op: WebSocket will deliver it eventually
        }
      },
    }),

    // -----------------------------------------------------------------------
    // Delete a report
    // -----------------------------------------------------------------------
    deleteReport: build.mutation<void, ProjectIdParam & { report_id: Uuid }>({
      query: ({ project_id, report_id }) => ({
        url: `${project_id}/${report_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: Tag.Reports, id: project_id },
        { type: Tag.Counts, id: project_id },
        { type: Tag.EndpointCounts, id: project_id },
      ],
      async onQueryStarted(
        { project_id, report_id },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(
          netDiagnosticsApi.util.updateQueryData(
            "reportsWs",
            { project_id },
            (draft) => {
              const idx = draft.findIndex((r) => r.id === report_id)
              if (idx !== -1) draft.splice(idx, 1)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    // -----------------------------------------------------------------------
    // Load more reports (appends older reports to the reportsWs cache)
    // -----------------------------------------------------------------------
    loadMoreReports: build.mutation<
      NetDiagnostic[],
      ProjectIdParam & { offset: number }
    >({
      query: ({ project_id, offset }) => ({
        url: project_id,
        params: { limit: 50, offset },
      }),
      async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
        try {
          const { data: older } = await queryFulfilled
          dispatch(
            netDiagnosticsApi.util.updateQueryData(
              "reportsWs",
              { project_id },
              (draft) => {
                const existing = new Set(draft.map((r) => r.id))
                for (const r of older) {
                  if (!existing.has(r.id)) draft.push(r)
                }
              },
            ),
          )
        } catch {
          // no-op
        }
      },
    }),
  }),
})

export const {
  useOnlineEndpointsWsQuery,
  useReportsWsQuery,
  useReportCountsQuery,
  useEndpointReportCountQuery,
  useLatestEndpointReportQuery,
  useEndpointReportsQuery,
  useGetReportQuery,
  useRunDiagnosticMutation,
  useDeleteReportMutation,
  useLoadMoreReportsMutation,
} = netDiagnosticsApi
